import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isFreeUser } from './utils/planAccess';
import {
    CircularProgressbarWithChildren,
    buildStyles
  } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { LightBulbIcon } from '@heroicons/react/solid';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import Modal from './components/Modal';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

  const leaderLearning = {
    autonomy: {
        title: "Autonomy",
        description: "Autonomy refers to an individual’s sense of volition, choice, and ownership over their actions and decisions. It's different than independence; the feeling that one's thoughts and behaviors are self-endorsed rather than externally imposed. When autonomy is supported, individuals are more motivated, engaged, and invested in their growth. \n\n In the context of a conversation, autonomy means ensuring that the employee feels like an active participant rather than a passive recipient of evaluation. A leader fosters autonomy by inviting the employee’s perspective, allowing room for self-reflection, and encouraging them to contribute to solutions. High-quality autonomy support includes asking open-ended questions, acknowledging their viewpoints, and providing choices instead of directives. In contrast, a low-autonomy conversation is controlling, one-sided, or overly prescriptive, where the leader dominates the discussion without considering the employee’s input. A collaborative approach builds trust and improves the quality of motivation, empowering employees to feel ownership for their development."
    },
    relatedness: {
        title: "Relatedness",
        description: "Relatedness refers to an individual’s feeling of connectedness, value, and support by others. It reflects the sense of belonging in relationships, where individuals feel seen, heard, and respected. In a work environment, relatedness enhances engagement, teamwork, and trust. \n\n In a conversation, relatedness means making the employee feel that the discussion is not just about evaluation but about mutual understanding and growth. A leader cultivates relatedness by actively listening, maintaining an open and nonjudgmental tone, and demonstrating genuine care for the employee’s experiences. High-quality relatedness in feedback means acknowledging their emotions, recognizing their contributions, and fostering a dialogue where they feel comfortable expressing concerns. When relatedness is low, feedback can feel impersonal, dismissive, or overly transactional, making the employee feel isolated or undervalued. Strengthening relatedness builds trust and psychological safety, allowing employees to engage openly in the conversation."
    },
    competence: {
        title: "Competence",
        description: "Competence refers to an individual’s feeling of effectiveness, capability, and confidence in their abilities. When competence is supported, individuals feel motivated to take on challenges, improve their skills, and pursue growth. A lack of competence support can lead to frustration, disengagement, or self-doubt. \n\n In a conversation, competence means ensuring the employee walks away feeling capable, encouraged, and clear about how to improve. A leader supports competence by offering specific, constructive feedback that highlights both achievements and areas for growth. High-quality competence support includes providing actionable suggestions, using concrete examples, and expressing confidence in the employee’s ability to develop. When competence support is weak, feedback may feel vague, overly critical, or lacking clear direction, which can leave employees uncertain about how to progress. Effective feedback should inspire confidence, provide clarity, and reinforce the employee’s ability to succeed in their role."        
    },
    microskillsFeedback: {
        title: "Feedback Quality",
        description: "Feedback is defined as information about past actions or behaviors, provided in the present, with the intention of guiding future performance. High-quality feedback is characterized by clarity, specificity, and relevance, helping the employee understand their strengths and identify areas for growth. Effective feedback is both constructive and autonomy-supportive, meaning it provides insights without being overly controlling or limiting the employee’s sense of choice or volition. By framing feedback in a way that respects the employee’s autonomy, the leader fosters a sense of ownership, making it more likely the employee will reflect on and act upon the feedback. \n\n Feedback can vary in form—positive or negative, immediate or delayed, future-oriented or reflective of past actions. Generally, feedback is better received when it is objective, grounded in specific observations rather than unsupported judgments or opinions. Objectivity helps ensure that feedback feels fair, whereas unsupported opinions can seem controlling and diminish the employee’s motivation. However, judgment can play a role in feedback if conclusions are clearly based on observed behaviors and outcomes; this approach maintains a constructive, respectful tone while guiding improvement. \n\n To be effective, feedback should ideally focus on behaviors and outcomes rather than personal attributes, giving the employee actionable insights that they can use to enhance their performance. Low-quality feedback, by contrast, is often vague, overly general, or poorly timed, leading to confusion or frustration. By providing specific, autonomy-supportive feedback, the leader not only communicates respect but also promotes the employee’s development and readiness to engage in future challenges."
    },
    activeListening: {
        title: "Active Listening",
        description: "Active listening is a leader’s ability to engage with an employee’s communication with a genuine intention to understand their perspective. Effective listening goes beyond hearing words; it’s about entering the conversation with the primary goal of understanding the employee’s viewpoint, both intellectually and emotionally. This process involves not only reflecting on verbal and non-verbal cues but also responding in ways that assure the employee they are truly heard. Techniques like reflecting feelings, paraphrasing key points, asking clarifying questions, and summarizing demonstrate that understanding is the priority. \n\n For active listening to be high quality, it must create a feeling of understanding in the employee. This feeling is essential, as it forms the foundation of connection, trust, and alignment, which in turn make employees more open to receiving feedback and considering suggestions for improvement. When an employee feels understood, they are more likely to engage openly, embrace new ideas, and participate in collaborative problem-solving. Conversely, low-quality listening, marked by interruptions, dismissiveness, or a lack of attentiveness, disrupts trust and discourages open communication. By prioritizing understanding first, the leader creates a safe, supportive environment that fosters meaningful dialogue and drives shared goals forward."    
    },
    problemSolving: {
        title: "Facilitating Problem Solving",
        description: "Facilitating problem solving in leadership is the skill of guiding an employee through a constructive approach to identifying and addressing challenges. It’s not about solving the problem for the employee or prescribing a solution but rather about empowering them to find their own solutions with your support. Effective problem-solving facilitation requires a balanced approach that adapts to the employee’s level of experience and ability: the more skilled and capable the employee, the less directive the leader needs to be, allowing more room for autonomy and self-discovery. At its core, facilitating problem solving is a collaborative process that builds the employee’s confidence, sense of ownership, and problem-solving abilities.\n\n High-quality facilitation helps the employee thoroughly analyze the issue, consider a range of possible solutions, and engage in making informed decisions. This process is dynamic and may involve moving back into active listening, as new thoughts or feelings emerge that need resolution before progress can be made. In this way, facilitation fosters an environment of mutual respect and engagement, where the employee feels safe to explore solutions and develop skills. Poor facilitation, on the other hand—such as dictating solutions or dismissing the employee’s ideas—can undermine their autonomy and diminish their sense of ownership and motivation. \n\n Ultimately, effective problem-solving facilitation leads to greater buy-in from the employee and reinforces their autonomy. By actively involving them in the problem-solving process, the leader helps the employee build the confidence, skills, and independence necessary to handle future challenges, thereby strengthening both individual growth and team collaboration."
    },
    styleSummary: {
        title: "Leadership Style Summary",
        description: "Effective leadership includes the ability to assess and adjust leadership behaviors to meet an employee's specific needs, based on their current ability (skills and knowledge) and energy (motivation and confidence). This concept is rooted in contingency leadership theory, which emphasizes aligning leadership approaches to changing circumstances for optimal results. \n\n An effective leader situationally adjusts their focused and inspiring behaviors depending on the employee’s situation. When an employee has lower ability, focused behaviors provide the structure and clarity needed to build competence. In contrast, when an employee has low energy, inspiring behaviors help reignite motivation and build confidence.\n\n Effective leadership combines both styles, tailoring the approach to simultaneously address skill gaps and motivational needs. This balance fosters a productive and supportive environment, enabling employees to perform at their best while feeling valued and empowered."
    },
    focused: {
        title: "Focused Leadership Style",
        description: "Focused leadership behavior is defined by directive actions aimed at providing clear guidance and structure to employees. More focused behaviors should be used as an employee exhibits lower ability related to discussion topic, whether due to inexperience, lack of training, or unfamiliarity with a task. The goal is to gain alignment between thier perceptions of ability and your focused behaviors to equip the employee with the knowledge and direction necessary to achieve success.\n\n A focused leader sets specific and measurable goals to establish clear expectations, provides detailed instructions, and demonstrates tasks when necessary. They clarify roles and responsibilities to eliminate ambiguity while developing actionable plans and timelines to guide progress. Monitoring performance closely and offering constructive feedback are essential to ensuring alignment with objectives and addressing areas for improvement. While providing this level of guidance, it is critical to have alignment with the employee to avoid coming across overly controlling or 'micro-managing', as this can inhibit the employee’s growth and autonomy. A well-executed focused approach creates a foundation for learning, enabling employees to build their skills and grow in confidence."
    },
    inspiring: {
        title: "Inspiring Leadership Style",
        description: "Inspiring leadership behavior involves a supportive style that focuses on enhancing an employee’s motivation and confidence, particularly when their energy is low. Low energy can stem from uncertainty, disengagement, or a lack of belief in their own capabilities. This style seeks to reengage the employee and foster a positive emotional connection to their work.\n\n An inspiring leader actively listens to understand the employee’s concerns and perspectives, encourages input and collaboration to foster a sense of ownership, and recognizes accomplishments while providing affirming feedback to build confidence. By sharing the rationale behind decisions, the leader aligns individual goals with organizational objectives, creating a sense of shared purpose. They foster an open and trusting environment by being transparent and sharing relevant information, helping employees feel valued and supported. This approach ensures the conversation aligns with the employee's specific needs, helping to rebuild confidence and motivation while encouraging active engagement in their role."
    },
    emSelf: {
        title: "Emotional Management of Self",
        description: "Emotional Management (Self) refers to a leader’s ability to regulate their own emotions during a conversation. This involves staying composed, maintaining professionalism, and avoiding reactive behaviors even in moments of tension or disagreement. Effective self-management ensures that the leader’s emotions do not negatively influence the tone or outcome of the conversation.\n\n In a performance dialogue, managing your emotions means demonstrating awareness of your feelings, acknowledging them constructively when necessary, and ensuring they support rather than hinder the discussion. It also involves adapting your communication style to match the flow of the conversation, shifting between directive and collaborative approaches as appropriate. The ability to manage your emotions creates a calm and productive environment, allowing both parties to focus on solutions rather than conflict.",
    },
    socialUnderstanding: {
        title: "Understanding Emotions of Others",
        description: "Social Understanding refers to a leader’s ability to perceive, interpret, and understand the emotions and perspectives of the employee. It involves recognizing emotional cues, such as tone and language, and connecting these to the broader context of the employee’s situation. This skill requires cognitive empathy, enabling the leader to accurately identify what the employee is feeling and why.\n\n During a performance discussion, social understanding goes beyond surface-level recognition of emotions. It involves active listening, thoughtful questions to clarify perspectives, and an ability to recognize complexities in the employee’s feelings. By demonstrating a deep understanding of the employee’s emotional dynamics, leaders can foster a sense of trust and alignment, ensuring the conversation is both respectful and effective.",
    },
    emOther: {
        title: "Emotional Management of Others",
        description: "Emotion Management (Others) reflects a leader’s ability to recognize and regulate the emotional dynamics of the employee during a conversation. This includes noticing and acknowledging the employee’s emotions, validating their feelings, and responding in ways that help maintain a constructive dialogue. Leaders skilled in this area use empathy to connect with the employee and foster trust while guiding the conversation toward positive outcomes.\n\n In practice, this means being sensitive to the employee’s emotional state, whether it’s frustration, disengagement, or confusion, and taking steps to de-escalate tension or provide encouragement. Managing the emotions of others requires balancing emotional awareness with actions that build confidence and motivation. When done effectively, it helps the employee feel heard and supported, creating an open and collaborative interaction.",
    }
  };

  const formatDifficulty = (level) => {
    const num = parseInt(level);
    if (num === 1 || num === 2) return 'Beginner';
    if (num === 3) return 'Intermediate';
    if (num === 4 || num === 5) return 'Advanced';
    return 'Unknown';
  };

// Function to shape feedback data into a structured format for the report
function shapeFeedbackData(feedback) {
  
  const safeTopic = (label, field, ratingKey, key) => {
    if (!feedback[field]) return null;
    const section = feedback[field];
    if (!section) return null;

    return {
      key,
      name: label,
      rating: feedback.ratings?.[ratingKey] || "N/A",
      good: Array.isArray(section.good) ? section.good : [],
      bad: Array.isArray(section.bad) ? section.bad : [],
      recommendation: section.recommendation || "No recommendation available"
    };
  };

  return {
    executiveSummary: feedback.executiveSummary || "No summary available.",
    ratings: feedback.ratings || {},
    scores: {
      motivation: getAvg([
        feedback.ratings?.autonomyRating,
        feedback.ratings?.relatednessRating,
        feedback.ratings?.competenceRating
      ]),
      skills: getAvg([
        feedback.ratings?.microskillsFeedbackRating,
        feedback.ratings?.activeListeningRating,
        feedback.ratings?.problemSolvingRating
      ]),
      style: getAvg([
        feedback.ratings?.styleSummaryRating,
        feedback.ratings?.focusedRating,
        feedback.ratings?.inspiringRating
      ]),
      emotionalIntelligence: getAvg([
        feedback.ratings?.emSelfRating,
        feedback.ratings?.socialUnderstanding,
        feedback.ratings?.emOtherRating
      ])
    },
    groupedTopics: {
      motivation: [
        safeTopic("Autonomy", "autonomy", "autonomyRating", "autonomy"),
        safeTopic("Relatedness", "relatedness", "relatednessRating", "relatedness"),
        safeTopic("Competence", "competence", "competenceRating", "competence")
      ].filter(Boolean),
      skills: [
        safeTopic("Feedback", "microskillsFeedback", "microskillsFeedbackRating", "microskillsFeedback"),
        safeTopic("Active Listening", "activeListening", "activeListeningRating", "activeListening"),
        safeTopic("Problem Solving", "problemSolving", "problemSolvingRating", "problemSolving")
      ].filter(Boolean),
      style: [
        safeTopic("Overall Style", "styleSummary", "styleSummaryRating", "styleSummary"),
        safeTopic("Focused Style", "focused", "focusedRating", "focused"),
        safeTopic("Inspiring Style", "inspiring", "inspiringRating", "inspiring")
      ].filter(Boolean),
      emotionalIntelligence: [
        safeTopic("Self Management", "emSelf", "emSelfRating", "emSelf"),
        safeTopic("Understanding Others", "socialUnderstanding", "socialUnderstanding", "socialUnderstanding"),
        safeTopic("Managing Others", "emOther", "emOtherRating", "emOther")
      ].filter(Boolean)
    }
  };
}

// Function to generate radar chart data from ratings
const getRadarData = (ratings = {}) => {
  
  const safe = (key) => {
    const val = ratings?.[key];
    const num = parseFloat(val);

    return isNaN(num) ? 0 : num;
  };

  return {
    labels: [
      'Autonomy', 'Relatedness', 'Competence',
      'Feedback', 'Active Listening', 'Problem Solving',
      'Focused', 'Inspiring', 'Style Summary',
      'Self Management', 'Understanding Others', 'Managing Others'
    ],
    datasets: [
      {
        label: 'Motivation',
        data: [
          safe('autonomyRating'),
          safe('relatednessRating'),
          safe('competenceRating'),
          ...Array(9).fill(null)
        ],
        borderColor: 'rgba(34,197,94,1)',
        pointBackgroundColor: 'rgba(34,197,94,1)',
        borderWidth: 2
      },
      {
        label: 'Leadership Skills',
        data: [
          ...Array(3).fill(null),
          safe('microskillsFeedbackRating'),
          safe('activeListeningRating'),
          safe('problemSolvingRating'),
          ...Array(6).fill(null)
        ],
        borderColor: 'rgba(59,130,246,1)',
        pointBackgroundColor: 'rgba(59,130,246,1)',
        borderWidth: 2
      },
      {
        label: 'Leadership Style',
        data: [
          ...Array(6).fill(null),
          safe('focusedRating'),
          safe('inspiringRating'),
          safe('styleSummaryRating'),
          ...Array(3).fill(null)
        ],
        borderColor: 'rgba(234,179,8,1)',
        pointBackgroundColor: 'rgba(234,179,8,1)',
        borderWidth: 2
      },
      {
        label: 'Emotional Intelligence',
        data: [
          ...Array(9).fill(null),
          safe('emSelfRating'),
          safe('socialUnderstanding'),
          safe('emOtherRating')
        ],
        borderColor: 'rgba(244,63,94,1)',
        pointBackgroundColor: 'rgba(244,63,94,1)',
        borderWidth: 2
      }
    ]
  };
};

function getAvg(values) {
  const nums = values.map(val => parseFloat(val)).filter(val => !isNaN(val));
  if (nums.length === 0) return 0;
  const sum = nums.reduce((a, b) => a + b, 0);
  return Math.round((sum / nums.length) * 10) / 10; // round to nearest tenth
}

// SubtopicBlock component to display individual topics in the tab format
const SubtopicBlock = ({ topic }) => {
  const [showModal, setShowModal] = useState(false);

  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  return (
    <div className="relative bg-gray-50 border border-gray-200 shadow-sm rounded-md p-4 w-full sm:w-[32%]">
      <div className="absolute top-3 right-3">
        <button
          onClick={handleOpen}
          title={`Click to learn about ${topic.name}`}
          className="p-1 bg-yellow-100 rounded-full hover:bg-yellow-200 transition transform hover:scale-110 shadow-sm"
        >
          <LightBulbIcon className="h-5 w-5 text-yellow-600" />
        </button>
      </div>

      <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
        {topic.name}
        <div className="flex gap-1">
          {[...Array(7)].map((_, i) => {
            const val = Math.round(topic.rating);
            let fillColor = "bg-gray-300";
            if (i < val) {
              if (val <= 2) fillColor = "bg-red-500";
              else if (val <= 5) fillColor = "bg-yellow-400";
              else fillColor = "bg-green-500";
            }
            return <span key={i} className={`w-2.5 h-2.5 rounded-full ${fillColor}`} />;
          })}
        </div>
      </h4>

      <div className="mb-1">
        <p className="font-semibold text-purple-700 text-sm">What Went Well</p>
        <ul className="list-disc ml-5 text-xs text-gray-700">
          {(isFreeUser() ? topic.good.slice(0, 1) : topic.good).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
          {isFreeUser() && topic.good.length > 1 && (
            <li className="text-gray-400 italic">Upgrade to see more feedback 🔒</li>
          )}
        </ul>
      </div>

      <div className="mb-1">
        <p className="font-semibold text-purple-700 text-sm">Areas to Improve</p>
        <ul className="list-disc ml-5 text-xs text-gray-700">
          {(isFreeUser() ? topic.bad.slice(0, 1) : topic.bad).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
          {isFreeUser() && topic.bad.length > 1 && (
            <li className="text-gray-400 italic">Upgrade to see more feedback 🔒</li>
          )}
        </ul>
      </div>

      <div>
        <p className="font-semibold text-purple-700 text-sm">Recommendations</p>
        <p className="text-xs text-gray-700 ml-2">{topic.recommendation}</p>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-md shadow-lg max-w-lg p-6 animate-fade-in">
            <h3 className="text-xl font-bold mb-2 text-gray-800">{topic.name}</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {leaderLearning[topic.key]?.description || "No description available."}
            </p>
            <div className="mt-4 text-right">
              <button
                onClick={handleClose}
                className="text-sm px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ReportPage to pull the feedback and generate the main report view
const ReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({});
  const [activeTab, setActiveTab] = useState("motivation");
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showDialogueModal, setShowDialogueModal] = useState(false);

  const dispositionValueMap = {
    ew: {
      "Highly Engaging": { value: 1, left: "Engage", right: "Withdraw" },
      "Engaging": { value: 2, left: "Engage", right: "Withdraw" },
      "Balanced": { value: 3, left: "Engage", right: "Withdraw" },
      "Withdrawing": { value: 4, left: "Engage", right: "Withdraw" },
      "Highly Withdrawing": { value: 5, left: "Engage", right: "Withdraw" },
    },
    ac: {
      "Highly Accepting": { value: 1, left: "Accepting", right: "Controlling" },
      "Accepting": { value: 2, left: "Accepting", right: "Controlling" },
      "Balanced": { value: 3, left: "Accepting", right: "Controlling" },
      "Controlling": { value: 4, left: "Accepting", right: "Controlling" },
      "Highly Controlling": { value: 5, left: "Accepting", right: "Controlling" },
    },
    ei: {
      "Highly Extroverted": { value: 1, left: "Extroverted", right: "Introverted" },
      "Extroverted": { value: 2, left: "Extroverted", right: "Introverted" },
      "Balanced": { value: 3, left: "Extroverted", right: "Introverted" },
      "Introverted": { value: 4, left: "Extroverted", right: "Introverted" },
      "Highly Introverted": { value: 5, left: "Extroverted", right: "Introverted" },
    },
    di: {
      "Highly Direct": { value: 1, left: "Direct", right: "Indirect" },
      "Direct": { value: 2, left: "Direct", right: "Indirect" },
      "Balanced": { value: 3, left: "Direct", right: "Indirect" },
      "Indirect": { value: 4, left: "Direct", right: "Indirect" },
      "Highly Indirect": { value: 5, left: "Direct", right: "Indirect" },
    },
    pj: {
      "Highly Percieving": { value: 1, left: "Perceiving", right: "Judging" },
      "Percieving": { value: 2, left: "Perceiving", right: "Judging" },
      "Balanced": { value: 3, left: "Perceiving", right: "Judging" },
      "Judging": { value: 4, left: "Perceiving", right: "Judging" },
      "Highly Judging": { value: 5, left: "Perceiving", right: "Judging" },
    },
    rt: {
      "Highly Risk-Taking": { value: 1, left: "Risk-Taking", right: "Risk-Assessing" },
      "Risk-Taking": { value: 2, left: "Risk-Taking", right: "Risk-Assessing" },
      "Balanced": { value: 3, left: "Risk-Taking", right: "Risk-Assessing" },
      "Risk-Assessing": { value: 4, left: "Risk-Taking", right: "Risk-Assessing" },
      "Highly Risk-Assessing": { value: 5, left: "Risk-Taking", right: "Risk-Assessing" },
    },
    op: {
      "Highly Optimistic": { value: 1, left: "Optimistic", right: "Pessimistic" },
      "Optimistic": { value: 2, left: "Optimistic", right: "Pessimistic" },
      "Balanced": { value: 3, left: "Optimistic", right: "Pessimistic" },
      "Pessimistic": { value: 4, left: "Optimistic", right: "Pessimistic" },
      "Highly Pessimistic": { value: 5, left: "Optimistic", right: "Pessimistic" },
    },
    co: {
      "Highly Change-Oriented": { value: 1, left: "Change-Oriented", right: "Continuity-Oriented" },
      "Change-Oriented": { value: 2, left: "Change-Oriented", right: "Continuity-Oriented" },
      "Balanced": { value: 3, left: "Change-Oriented", right: "Continuity-Oriented" },
      "Continuity-Oriented": { value: 4, left: "Change-Oriented", right: "Continuity-Oriented" },
      "Highly Continuity-Oriented": { value: 5, left: "Change-Oriented", right: "Continuity-Oriented" },
    },
    tp: {
      "Highly Task-Focused": { value: 1, left: "Task-Focused", right: "People-Focused" },
      "Task-Focused": { value: 2, left: "Task-Focused", right: "People-Focused" },
      "Balanced": { value: 3, left: "Task-Focused", right: "People-Focused" },
      "People-Focused": { value: 4, left: "Task-Focused", right: "People-Focused" },
      "Highly People-Focused": { value: 5, left: "Task-Focused", right: "People-Focused" },
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/feedback/get-all-feedback?conversationId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
    
        if (!response.ok) {
          throw new Error("Failed to fetch report data");
        }
    
        const report = await response.json();
        console.log('feedback response', report);
    
        if (!report || !report.conversationId) {
          throw new Error("No report found for this conversation");
        }
    
        // Set metadata for the Title, Name, and Difficulty Level
        setMeta({
          scenarioTitle: report.scenarioTitle,
          employeeName: report.employeeName,
          difficultyLevel: report.difficultyLevel,
          scenarioDescription: report.scenarioDescription,
          scenarioSummary: report.scenarioSummary,
          profile: report.profile,
          conversationHistory: report.conversationHistory
        });
    
        setData(shapeFeedbackData(report));
      } catch (error) {
        console.error("Error fetching report:", error);
        setData(null);
      }
    };
    
  
    if (id) {
      fetchReport();
    }
  }, [id]);

  const tabLabels = {
    motivation: "Motivation",
    skills: "Leadership Skills",
    style: "Leadership Style",
    emotionalIntelligence: "Emotional Intelligence"
  };

  if (!data) return <div className="p-10 text-center">Loading Report...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <button className="text-blue-600 underline mb-4" onClick={() => navigate('/convo')}>
        &larr; Back to Conversations
      </button>
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Conversation Report</h1>

      <div className="flex items-center gap-4 text-sm text-gray-700 mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {meta?.scenarioTitle || 'Untitled'}
          {meta?.employeeName && (
            <span className="ml-2 italic font-normal text-gray-600">({meta.employeeName})</span>
          )}
        </h2>
        {meta?.difficultyLevel && (
          <span className="bg-white border border-gray-300 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
            {formatDifficulty(meta.difficultyLevel)}
          </span>
        )}
        <div className="flex-1 flex justify-end gap-3">
          <button
            onClick={() => setShowScenarioModal(true)}
            className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded shadow hover:bg-blue-700 transition"
          >
            View Scenario Summary
          </button>
          <button
            onClick={() => setShowDialogueModal(true)}
            className="bg-gray-600 text-white text-sm font-medium px-4 py-2 rounded shadow hover:bg-gray-700 transition"
          >
            View Conversation
          </button>
        </div>
      </div>

      {showScenarioModal && (
  <Modal title="Scenario Summary" onClose={() => setShowScenarioModal(false)}>
    <div className="space-y-4 text-sm text-gray-800">
      {/* Scenario Title */}
      <h2 className="text-lg font-bold">{meta?.scenarioTitle}</h2>

      {/* Scenario Description */}
      <p className="whitespace-pre-line">{meta?.scenarioDescription}</p>

      {/* Profile Details */}
      {meta?.profile && (
        <div className="grid grid-cols-[180px_1fr] gap-6 mt-4">
          {/* Interpersonal */}
          <div className="space-y-2">
            <p><strong>Familiarity:</strong> { { LF: "Low", MF: "Medium", HF: "High" }[meta.profile.familiarity] }</p>
            <p><strong>Emotion:</strong> { { LED: "Low", MED: "Medium", HED: "High" }[meta.profile.emotion] }</p>

            {/* Trust Breakdown */}
            {(() => {
              const trustMap = {
                clel: { head: 'Low', heart: 'Low' },
                clem: { head: 'Low', heart: 'Medium' },
                cleh: { head: 'Low', heart: 'High' },
                cmel: { head: 'Medium', heart: 'Low' },
                cmem: { head: 'Medium', heart: 'Medium' },
                cmeh: { head: 'Medium', heart: 'High' },
                chel: { head: 'High', heart: 'Low' },
                chem: { head: 'High', heart: 'Medium' },
                cheh: { head: 'High', heart: 'High' }
              };
              const trustKey = meta.profile.trust?.toLowerCase();
              const trust = trustMap[trustKey] || {};
              return (
                <>
                  <p><strong>Head Trust:</strong> {trust.head || "Unknown"}</p>
                  <p><strong>Heart Trust:</strong> {trust.heart || "Unknown"}</p>
                </>
              );
            })()}
          </div>

          {/* Dispositions */}
          <div className="space-y-2">
            {Object.entries(meta.profile).filter(([key]) =>
              ['ew','ac','ei','di','pj','rt','op','co','tp'].includes(key)
            ).map(([key, level]) => {
              const traitMap = dispositionValueMap[key];
              if (!traitMap) return null;

              const match = Object.entries(traitMap).find(
                ([, info]) => info.value === Number(level)
              );

              const label = match?.[0];
              const trait = match?.[1];

              return (
                <p key={key}>
                  <strong>{trait?.left} / {trait?.right}:</strong> {label}
                </p>
              );
            })}
          </div>
        </div>
      )}
    </div>
  </Modal>
)}


{showDialogueModal && (
  <Modal title="Conversation Dialogue" onClose={() => setShowDialogueModal(false)}>
    <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2 text-sm text-gray-800">
      {(meta?.conversationHistory || []).map((msg, idx) => (
        <div key={idx} className="flex gap-2">
          <span className="font-medium text-gray-600 capitalize">
            {msg.role === 'user' ? 'You' : meta?.employeeName || 'Employee'}:
          </span>
          <span>{msg.content}</span>
        </div>
      ))}
    </div>
  </Modal>
)}

      <div className="bg-green-50 border-l-4 border-green-400 text-green-900 p-4 rounded mb-10 flex flex-col md:flex-row justify-between gap-6">
        <div className="flex-1">
          <h2 className="text-lg font-semibold mb-1">Executive Summary</h2>
          <p className="text-sm">{data.executiveSummary}</p>
        </div>
        <div className="flex-1 max-w-md mx-auto">
          <Radar
            data={getRadarData(data.ratings)}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom', // 👈 Moves legend below the chart
                  labels: {
                    boxWidth: 12,
                    padding: 15
                  }
                }
              },
              scales: {
                r: {
                  min: 0,
                  max: 7,
                  ticks: { stepSize: 1 },
                  pointLabels: {
                    font: { size: 12 }
                  }
                }
              }
            }}
          />
        </div>
      </div>


    {/* Tab Selector */}
    <div className="flex mb-0">
      {Object.keys(tabLabels).map((key) => {
        const locked = isFreeUser() && key !== "motivation";

        return (
          <button
            key={key}
            onClick={() => !locked && setActiveTab(key)}
            className={`relative flex-1 flex flex-col items-center py-4 transition border border-b-0 rounded-t-md ${
              activeTab === key
                ? "bg-white border-gray-300 border-b-white text-green-700 z-10"
                : locked
                ? "bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-800"
            }`}
          >
            {/* Lock in top right of tab */}
            {locked && (
              <div
                className="absolute top-2 right-2.5"
                title="Upgrade to access full report analysis"
              >
                <span className="text-gray-400 text-md">🔒</span>
              </div>
            )}

            {/* Score Chart */}
            <div className="w-12 h-12 mb-1">
              <CircularProgressbarWithChildren
                value={parseFloat(data.scores[key].toFixed(1))}
                maxValue={7}
                styles={buildStyles({
                  pathColor:
                    data.scores[key] <= 2.5
                      ? "rgba(239, 68, 68, 1)" // Red
                      : data.scores[key] <= 5.5
                      ? "rgba(234, 179, 8, 1)" // Yellow
                      : "rgba(34, 197, 94, 1)", // Green
                  trailColor: "#e6e6e6"
                })}
                
              >
                <div
                  className="text-sm font-bold"
                  style={{
                    color:
                      data.scores[key] <= 2.5
                        ? "rgba(239, 68, 68, 1)"
                        : data.scores[key] <= 5.5
                        ? "rgba(234, 179, 8, 1)"
                        : "rgba(34, 197, 94, 1)"
                  }}
                >
                  {data.scores[key].toFixed(1)}
                </div>
              </CircularProgressbarWithChildren>
            </div>

            <span className="text-xl font-medium">{tabLabels[key]}</span>
          </button>
        );
      })}
    </div>

        {/* Tab Content */}
        <div className="bg-white border border-t-0 border-gray-300 rounded-b-md p-6 mb-10">
            <div className="flex flex-wrap gap-4 justify-between">
            {(isFreeUser() && activeTab !== "motivation") ? (
              <div className="text-center text-gray-500 text-sm w-full py-6 italic">
                This section is only available for premium users. Upgrade to unlock full feedback 🔒
              </div>
            ) : (
              data.groupedTopics[activeTab].map((topic, idx) => (
                <SubtopicBlock key={idx} topic={topic} />
              ))
            )}
            </div>
        </div>
        </div>
    );
  };

export default ReportPage;
